import algoliasearch from 'algoliasearch/lite';
import instantsearch, { UiState } from 'instantsearch.js';
import {
  connectAutocomplete,
  connectClearRefinements,
  connectSearchBox,
} from 'instantsearch.js/es/connectors';
import { highlight } from 'instantsearch.js/es/helpers';
import { history } from 'instantsearch.js/es/lib/routers';
import {
  menu,
  hits,
  queryRuleCustomData,
  pagination,
  stats,
  configure,
} from 'instantsearch.js/es/widgets';

import { renderSuggestionsIndexListItem } from '@/src/utilities/search/searchRenderFunctions';

export function loadSiteSearch(): void {
  const config = window['site-search'];

  if (!config) {
    return;
  }

  const {
    applicationId,
    searchKey,
    searchContext,
    searchIndexName,
    suggestionsIndexName,
    placeholderImageURL,
  } = config;

  const { searchTerm: query } = searchContext;

  const algoliaClient = algoliasearch(applicationId, searchKey);

  const searchClient = {
    ...algoliaClient,
    search(requests: any[]) {
      return algoliaClient.search(requests);
    },
  };

  const search = instantsearch({
    indexName: searchIndexName,
    searchClient,
    insights: true,
    routing: {
      router: history({
        createURL({ qsModule, routeState, location }) {
          const urlParts = location.href.match(/^(.*?)\/search/);
          const baseUrl = `${urlParts ? urlParts[1] : ''}/`;

          type QueryParams = {
            term?: string;
            resultType?: string;
            page?: number;
          };

          const queryParameters = {} as QueryParams;
          const pageNumber = routeState['page'] as number;

          // set the query
          if (routeState['term']) {
            queryParameters.term = routeState['term'] as string;
          }

          // resultType facet
          if (routeState['resultType']) {
            queryParameters.resultType = routeState['resultType'] as string;
          }

          // set the page number
          if (pageNumber !== 1) {
            queryParameters.page = pageNumber;
          }

          const queryString = qsModule.stringify(queryParameters, {
            addQueryPrefix: true,
            arrayFormat: 'repeat',
          });

          return `${baseUrl}search${queryString.split('%20').join('+')}`;
        },

        parseURL({ qsModule, location }): UiState {
          const {
            query = '',
            resultType = '',
            page,
          } = qsModule.parse(location.search.slice(1));
          // `qs` does not return an array when there's a single value.

          return {
            term: decodeURIComponent(query as string),
            resultType: resultType,
            page,
          };
        },
      }),

      stateMapping: {
        stateToRoute(uiState: UiState): RouteState {
          const indexUiState = uiState[searchIndexName];

          const resultType =
            indexUiState?.menu &&
            indexUiState?.menu['ClassName'] === 'URMC.LocationPage'
              ? 'Locations'
              : indexUiState?.menu &&
                indexUiState?.menu['ClassName'] === 'URMC.ConditionPage'
              ? 'Conditions and Treatments'
              : indexUiState?.menu &&
                indexUiState?.menu['ClassName'] === 'URMC.ProviderPage'
              ? 'Providers'
              : indexUiState?.menu &&
                indexUiState?.menu['ClassName'] === 'URMC.BasicContentPage'
              ? 'Information'
              : '';

          return {
            term: indexUiState?.query,
            resultType: resultType,
            page: indexUiState?.page,
          };
        },
        routeToState(routeState: UiState): UiState {
          const resultType =
            routeState['resultType']?.toString() === 'Locations'
              ? 'URMC.LocationPage'
              : routeState['resultType']?.toString() ===
                'Conditions and Treatments'
              ? 'URMC.ConditionPage'
              : routeState['resultType']?.toString() === 'Providers'
              ? 'URMC.ProviderPage'
              : routeState['resultType']?.toString() === 'Information'
              ? 'URMC.BasicContentPage'
              : '';

          console.log("routeState['resultType']", routeState['resultType']);
          console.log('resultType', resultType);
          return {
            [searchIndexName]: {
              query: routeState['term'],
              menu: { ClassName: resultType },
              page: routeState['page'],
            },
          };
        },
      },
    },
  });

  type RouteState = {
    term: string | undefined;
    resultType: string | undefined;
    page: number | undefined;
  };

  let conditionHitsCount = 0;
  let locationHitsCount = 0;
  let providerHitsCount = 0;
  let infoHitsCount = 0;

  let totalHitsCount =
    conditionHitsCount + locationHitsCount + providerHitsCount + infoHitsCount;

  const url = new URL(window.location.href);

  // render functions:
  const renderAutocomplete = (
    renderOptions: {
      indices: object[];
      currentRefinement: string;
      refine: any; // should be a function
      widgetParams: any; // should be an object
    },
    isFirstRender: boolean
  ): void => {
    const { refine, widgetParams } = renderOptions;

    if (isFirstRender) {
      const aaForm = document.createElement('form'); // aa-Form

      const aaPanel = document.createElement('div'); // dropdown panel

      const suggestionsList = document.createElement('div'); // contains 1 li per hit/suggestion
      const providerList = document.createElement('div');
      const locationList = document.createElement('div');
      const conditionList = document.createElement('div');

      suggestionsList.classList.add('suggestions-list');
      providerList.classList.add('provider-list');
      locationList.classList.add('location-list');
      conditionList.classList.add('condition-list');

      aaPanel.appendChild(suggestionsList);
      aaPanel.appendChild(providerList);
      aaPanel.appendChild(locationList);
      aaPanel.appendChild(conditionList);

      const aaInputWrapperPrefix = document.createElement('div'); // aa-InputWrapperPrefix
      const aaLabel = document.createElement('label'); // aa-Label

      const aaExtendedSearchbox = document.createElement('div'); // aa-ExtendedSearchbox
      const aaClearButton = document.createElement('button'); // aa-ClearButton
      aaClearButton.setAttribute('aria-label', 'Clear search form');

      const aaSubmitButton = document.createElement(
        'button'
      ) as HTMLButtonElement; // aa-SubmitButton

      const aaLoadingIndicator = document.createElement('div'); // aa-LoadingIndicator
      const aaLoadingIcon = document.createElement('svg'); // aa-LoadingIcon

      const aaInput = document.createElement('input'); // aa-Input

      const aaInputWrapperSuffix = document.createElement('div'); // aa-InputWrapperSuffix

      aaInput.value = query;

      autocompleteSearch(query);

      aaInput.addEventListener('focus', (event) => {
        const eventTarget = event.target as HTMLInputElement;

        if (eventTarget.value.length > 0) {
          aaClearButton.classList.remove('hidden');
        }

        if (eventTarget.value.length > 1) {
          aaPanel.classList.remove('hidden');
        }
      });

      aaInput.addEventListener('input', (event) => {
        const eventTarget = event.target as HTMLInputElement;
        const hasResults = document.querySelector('.has-results');
        const checkVariable = hasResults ? true : false;

        autocompleteSearch(eventTarget.value);

        if (eventTarget.value.length > 1 && checkVariable) {
          aaPanel.classList.remove('hidden');
        } else {
          aaPanel.classList.add('hidden');
        }

        if (aaInput.value.length > 0) {
          aaClearButton.classList.remove('hidden');
        } else {
          aaClearButton.classList.add('hidden');
        }
      });

      let panelIsFocused = false;
      aaPanel.addEventListener('mousedown', (event) => {
        event.preventDefault();

        panelIsFocused = true;
      });

      aaClearButton.addEventListener('mousedown', (event) => {
        event.preventDefault();
        aaPanel.classList.add('hidden');
      });

      aaInput.addEventListener('blur', () => {
        searchContext.searchTerm = aaInput.value;
        autocompleteSearch(aaInput.value);

        if (!panelIsFocused) {
          aaPanel.classList.add('hidden');
        }
      });

      // add hover state on mouse enter
      // Add a single event listener to the parent element
      aaPanel.addEventListener(
        'mouseenter',
        (event) => {
          const target = event.target as HTMLElement;

          if (target.tagName === 'A') {
            target.classList.add('bg-stone-100', 'hover:underline');
          }
        },
        true
      ); // Use capture phase to ensure it catches the event

      // remove background color on mouse leave UNLESS it's the currently focused element
      // Add a single event listener to the parent element
      aaPanel.addEventListener(
        'mouseleave',
        (event) => {
          const target = event.target as HTMLElement;
          const currentFocus = document.querySelector('#selected-query');

          if (target.tagName === 'A') {
            if (target !== currentFocus) {
              target.classList.remove('bg-stone-100');
            }
          }
        },
        true
      ); // Use capture phase to ensure it catches the event

      // arrow key navigation
      aaInput.addEventListener('keydown', (event: KeyboardEvent) => {
        const focusableEls = Array.prototype.slice.call(
          aaPanel.querySelectorAll('a')
        );
        const lastFocusableEl = focusableEls[focusableEls.length - 1];

        const currentFocus = document.querySelector('#selected-query');
        let currentIndex = focusableEls.indexOf(currentFocus);
        const nextFocusableEl = focusableEls[currentIndex + 1];
        const prevFocusableEl = focusableEls[currentIndex - 1];
        // togglePanel();

        if (event.key === 'ArrowDown' && aaInput.value.length > 1) {
          event.preventDefault();

          if (currentIndex === -1) {
            searchContext.searchTerm = aaInput.value;
          }

          aaPanel.classList.remove('hidden');

          currentFocus?.removeAttribute('id');

          // background color is not removed if the element is currently hovered
          if (!currentFocus?.matches(':hover')) {
            currentFocus?.classList.remove('bg-stone-100');
          }

          currentIndex++;

          if (!nextFocusableEl) {
            currentFocus?.removeAttribute('id');
            currentFocus?.classList.remove('bg-stone-100');

            aaPanel.scroll({ top: 0, behavior: 'smooth' });

            aaInput.value = searchContext.searchTerm;
          } else {
            nextFocusableEl.classList.add('bg-stone-100');

            nextFocusableEl.setAttribute('id', 'selected-query');

            nextFocusableEl.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });

            aaInput.value = nextFocusableEl.textContent.trim();
          }
        }

        if (event.key === 'ArrowUp' && aaInput.value.length > 1) {
          event.preventDefault();

          if (currentIndex === -1) {
            searchContext.searchTerm = aaInput.value;
          }

          aaPanel.classList.remove('hidden');

          currentFocus?.removeAttribute('id');

          // background color is not removed if the element is currently hovered
          if (!currentFocus?.matches(':hover')) {
            currentFocus?.classList.remove('bg-stone-100');
          }

          currentIndex--;

          if (!prevFocusableEl) {
            currentFocus?.removeAttribute('id');
            currentFocus?.classList.remove('bg-stone-100');

            if (!currentFocus) {
              lastFocusableEl.classList.add('bg-stone-100');

              lastFocusableEl.setAttribute('id', 'selected-query');

              lastFocusableEl.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
              });

              aaInput.value = lastFocusableEl.textContent.trim();
            } else {
              aaInput.value = searchContext.searchTerm;
            }
          } else {
            prevFocusableEl.classList.add('bg-stone-100');

            prevFocusableEl.setAttribute('id', 'selected-query');

            prevFocusableEl.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });

            aaInput.value = prevFocusableEl.textContent.trim();
          }
        }

        if (event.key === 'Enter') {
          event.preventDefault();

          if (currentFocus?.hasAttribute('data-app-autocomplete-result')) {
            const url = currentFocus.getAttribute('href') as string;
            window.location.href = url;
          } else {
            if (currentFocus) {
              const selectedQuery = currentFocus.textContent?.trim() as string;
              aaInput.value = selectedQuery;
            }
            refine(aaInput.value);
            autocompleteSearch(aaInput.value);
            aaPanel.classList.add('hidden');
          }
        }
      });

      aaClearButton.addEventListener('click', (event) => {
        event.preventDefault();
        aaInput.value = '';
        aaClearButton.classList.add('hidden');
      });

      // autocomplete panel section
      // aaPanel, ul
      aaPanel.setAttribute(
        'class',
        'aa-Panel search-autocomplete-panel z-50 w-full hidden'
      );

      aaForm.setAttribute('class', 'lg:flex gap-4 items-start');

      aaLoadingIndicator.setAttribute('class', 'aa-LoadingIndicator');
      aaLoadingIcon.setAttribute('class', 'aa-LoadingIcon');

      // prefix section
      // aaInput, aaLabel, aaClearButton
      aaExtendedSearchbox.setAttribute(
        'class',
        'aa-ExtendedSearchbox flex items-center relative'
      );

      const toggleQueryClearButton = aaInput.value.length > 0 ? '' : 'hidden';

      aaClearButton.setAttribute(
        'class',
        `-ml-7 ${toggleQueryClearButton} group absolute right-3`
      );

      aaClearButton.innerHTML = `
          <svg width="16" height="15" viewBox="0 0 16 15" class="w-4 h-4" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path class="fill-stone-400 group-hover:fill-current" d="M0.304688 7.5C0.304688 3.35742 3.66211 0 7.80469 0C11.9473 0 15.3047 3.35742 15.3047 7.5C15.3047 11.6426 11.9473 15 7.80469 15C3.66211 15 0.304688 11.6426 0.304688 7.5ZM5.43164 6.09668L6.81152 7.47363L5.43164 8.87695C5.15918 9.15234 5.15918 9.59766 5.43164 9.84668C5.70703 10.1455 6.15234 10.1455 6.40137 9.84668L7.77832 8.49316L9.18164 9.84668C9.45703 10.1455 9.90234 10.1455 10.1514 9.84668C10.4502 9.59766 10.4502 9.15234 10.1514 8.87695L8.79785 7.47363L10.1514 6.09668C10.4502 5.84766 10.4502 5.40234 10.1514 5.12695C9.90234 4.85449 9.45703 4.85449 9.18164 5.12695L7.77832 6.50684L6.40137 5.12695C6.15234 4.85449 5.70703 4.85449 5.43164 5.12695C5.15918 5.40234 5.15918 5.84766 5.43164 6.09668Z"/>
          </svg>`;

      aaInputWrapperPrefix.setAttribute(
        'class',
        'aaInputWrapperPrefix flex-1 relative'
      );

      aaLabel.setAttribute('for', 'urmcQuery');
      aaLabel.setAttribute('class', 'mb-3 font-semibold inline-block');
      aaLabel.innerHTML = `Search UR Medicine <span class="font-normal text-gray-700 italic block sm:ml-3 sm:inline-block">Providers, locations, conditions, etc.</span>`;

      aaInput.setAttribute(
        'class',
        'border rounded w-full p-3 leading-snug border-stone-400'
      );
      aaInput.setAttribute('id', 'urmcQuery');
      aaInput.setAttribute('name', 'urmcQuery');

      aaInput.setAttribute('type', 'text');
      aaInput.setAttribute('autocomplete', 'off');

      // suffix section
      // aaSubmitButton
      const spacer = document.createElement('div');
      spacer.setAttribute('class', 'search-btn-spacer');

      aaSubmitButton.setAttribute(
        'class',
        'btn btn-primary my-4 w-full max-w-none lg:my-0'
      );
      aaSubmitButton.setAttribute('aria-label', 'Search website');
      aaSubmitButton.innerHTML = 'Search';

      aaForm.onsubmit = (event) => {
        event.preventDefault();
        refine(aaInput.value);
      };

      if (
        typeof widgetParams.container === 'object' &&
        widgetParams.container !== null &&
        'appendChild' in widgetParams.container
      ) {
        widgetParams.container.appendChild(aaForm);
        widgetParams.container.appendChild(aaLoadingIndicator);

        // aaForm section
        aaForm.appendChild(aaInputWrapperPrefix);
        // aaForm.appendChild(aaInputWrapper);
        aaForm.appendChild(aaInputWrapperSuffix);

        // aaExtendedSearchbox
        aaExtendedSearchbox.appendChild(aaInput);
        aaExtendedSearchbox.appendChild(aaClearButton);

        // prefix
        aaInputWrapperPrefix.appendChild(aaLabel);
        aaInputWrapperPrefix.appendChild(aaExtendedSearchbox);
        aaInputWrapperPrefix.appendChild(aaPanel);

        // suffix
        aaInputWrapperSuffix.appendChild(spacer);
        aaInputWrapperSuffix.appendChild(aaSubmitButton);
      }

      refine(aaInput.value);

      // end of isFirstRender conditional
    }
  };

  const renderProviderIndexListItem = ({ indexId, hits }) => `
      <div class="px-4 py-2 uppercase text-sm font-bold ${
        hits.length > 0 ? 'has-results' : 'hidden'
      }">Results for Providers</div>
      <ol class="list-none">
        ${hits
          .map(
            (hit: any) =>
              `
                <li>
                  <a data-app-autocomplete-result class="p-4 block w-full" tabindex="-1"
                  href="${hit['Url']}"
                  >
                    ${highlight({
                      attribute: 'Title',
                      hit,
                    })}
                  </a>
                </li>
              `
          )
          .join('')}
      </ol>
      `;

  const renderLocationIndexListItem = ({ indexId, hits }) => `
      <div class="px-4 py-2 uppercase text-sm font-bold ${
        hits.length > 0 ? 'has-results' : 'hidden'
      }">Results for locations</div>
      <ol class="list-none">
        ${hits
          .map(
            (hit: any) =>
              `
                <li>
                  <a data-app-autocomplete-result class="p-4 block w-full" tabindex="-1"
                  href="${hit['Url']}"
                  >
                    ${highlight({
                      attribute: 'Title',
                      hit,
                    })} ${
                hit['Address']?.Town ? `- ${hit['Address']?.Town}` : ''
              }
                  </a>
                </li>
              `
          )
          .join('')}
      </ol>
      `;

  const renderConditionIndexListItem = ({ indexId, hits }) => `
      <div class="px-4 py-2 uppercase text-sm font-bold ${
        hits.length > 0 ? 'has-results' : 'hidden'
      }">Results for Conditions & Treatments</div>
      <ol class="list-none">
        ${hits
          .map(
            (hit: any) =>
              `
                <li>
                  <a data-app-autocomplete-result class="p-4 block w-full" tabindex="-1"
                  href="${hit['Url']}"
                  >
                    ${highlight({
                      attribute: 'Title',
                      hit,
                    })}
                  </a>
                </li>
              `
          )
          .join('')}
      </ol>
      `;

  const createDataAttributes = (refinement: any) =>
    Object.keys(refinement)
      .map((key) => `data-${key}="${refinement[key]}"`)
      .join(' ');

  const renderListItem = (item: any) =>
    `
      ${item.refinements
        .map(
          (refinement: any) =>
            `<li class="ais-CurrentRefinements-category filter-pill">
              ${
                refinement.attribute === 'IsAcceptingNewPatients' &&
                refinement.value === 'true'
                  ? 'Accepting New Patients'
                  : refinement.attribute === 'IsAllowOpenScheduling' &&
                    refinement.value === 'true'
                  ? 'Has Online Scheduling'
                  : refinement.attribute === 'Gender' &&
                    refinement.value === 'M'
                  ? 'Male'
                  : refinement.attribute === 'Gender' &&
                    refinement.value === 'F'
                  ? 'Female'
                  : refinement.label
              }
              <button class="text-pacific-blue ml-2" ${createDataAttributes(
                refinement
              )}>✕</button>
            </li>`
        )
        .join('')}
      `;

  const renderCurrentRefinements = (renderOptions: {
    items: any;
    refine: any;
    widgetParams: any;
  }) => {
    const { items, refine, widgetParams } = renderOptions;

    const container = widgetParams.container;

    if (
      typeof container === 'object' &&
      container !== null &&
      'appendChild' in container
    ) {
      container.innerHTML = `
              <ul class="filter-list list-none">
                ${items.map(renderListItem).join('')}
              </ul>
            `;

      [...container.querySelectorAll('button')].forEach((element) => {
        element.addEventListener(
          'click',
          (event: { currentTarget: { dataset: { [x: string]: any } } }) => {
            const item = Object.keys(event.currentTarget.dataset).reduce(
              (acc, key) => ({
                ...acc,
                [key]: event.currentTarget.dataset[key],
              }),
              {}
            );

            refine(item);
          }
        );
      });
    }
  };

  const renderClearFacets = (
    renderOptions: { canRefine: any; refine: any; widgetParams: any },
    isFirstRender: any
  ) => {
    const { refine, widgetParams } = renderOptions;

    if (isFirstRender) {
      const clearFacets = document.createElement('button');

      clearFacets.setAttribute('data-app-search-refinement', 'all-results');
      clearFacets.setAttribute(
        'class',
        `py-0.5 text-navy-blue text-lg transition-none hover:underline focus-visible:outline-offset-4 focus-visible:outline-current`
      );

      clearFacets.textContent = `All Results`;

      clearFacets.addEventListener('click', () => {
        refine();
      });

      widgetParams.container.appendChild(clearFacets);
    }

    const clearFacetsWrapper = document.querySelector(
      '[data-app-search-refinement="clear-facets"]'
    ) as HTMLElement;

    const clearFacets = document.querySelector(
      '[data-app-search-refinement="all-results"]'
    ) as HTMLElement;

    const filterSelected = document.querySelector('.ais-Menu-item--selected');

    const checkForFilter = filterSelected ? false : true;

    if (checkForFilter) {
      clearFacetsWrapper?.classList.add('border-pacific-blue');
      clearFacetsWrapper?.classList.remove('border-transparent');
      clearFacets?.classList.add('font-bold', 'text-pacific-blue');
    } else {
      clearFacetsWrapper?.classList.remove('border-pacific-blue');
      clearFacetsWrapper?.classList.add('border-transparent');
      clearFacets?.classList.remove('font-bold', 'text-pacific-blue');
    }
  };

  const customClearFacets = connectClearRefinements(renderClearFacets);

  // utils:

  function updateTotalHitsCount(dataValue: string, classCount: number) {
    if (dataValue === 'URMC.ConditionPage') {
      conditionHitsCount = classCount;
    }
    if (dataValue === 'URMC.LocationPage') {
      locationHitsCount = classCount;
    }
    if (dataValue === 'URMC.ProviderPage') {
      providerHitsCount = classCount;
    }
    if (dataValue === 'URMC.BasicContentPage') {
      infoHitsCount = classCount;
    }

    totalHitsCount =
      conditionHitsCount +
      locationHitsCount +
      providerHitsCount +
      infoHitsCount;

    const allResultsButton = document.querySelector(
      '[data-app-search-refinement="all-results"]'
    ) as HTMLElement;

    allResultsButton.textContent = `All Results`;
  }

  function autocompleteSearch(query: string) {
    const suggestionsIndex = searchClient.initIndex(suggestionsIndexName);
    const resultsIndex = searchClient.initIndex(searchIndexName);

    suggestionsIndex
      .search(query, {
        hitsPerPage: 3,
      })
      .then(({ hits }) => {
        const suggestionsList = document.querySelector(
          '.suggestions-list'
        ) as HTMLElement;

        suggestionsList.innerHTML = renderSuggestionsIndexListItem({
          indexId: suggestionsIndexName,
          hits: hits,
        });
      });

    resultsIndex
      .search(query, {
        filters: 'ClassName:"URMC.ProviderPage"',
        hitsPerPage: 3,
      })
      .then(({ hits }) => {
        const resultsList = document.querySelector(
          '.provider-list'
        ) as HTMLElement;

        resultsList.innerHTML = renderProviderIndexListItem({
          indexId: searchIndexName,
          hits: hits,
        });
      });

    resultsIndex
      .search(query, {
        filters: 'ClassName:"URMC.LocationPage"',
        hitsPerPage: 3,
      })
      .then(({ hits }) => {
        const resultsList = document.querySelector(
          '.location-list'
        ) as HTMLElement;

        resultsList.innerHTML = renderLocationIndexListItem({
          indexId: searchIndexName,
          hits: hits,
        });
      });

    resultsIndex
      .search(query, {
        filters: 'ClassName:"URMC.ConditionPage"',
        hitsPerPage: 3,
      })
      .then(({ hits }) => {
        const resultsList = document.querySelector(
          '.condition-list'
        ) as HTMLElement;

        resultsList.innerHTML = renderConditionIndexListItem({
          indexId: searchIndexName,
          hits: hits,
        });
      });
  }

  function imageMouseEnter(id: string) {
    const profileLink = document.querySelector(`#link-${id}`);
    profileLink?.classList.add('underline', 'text-pacific-blue');
    profileLink?.classList.remove('no-underline');
  }

  function imageMouseLeave(id: string) {
    const profileLink = document.querySelector(`#link-${id}`);
    profileLink?.classList.remove('underline', 'text-pacific-blue');
    profileLink?.classList.add('no-underline');
  }

  const virtualSearchBox = connectSearchBox(() => null);
  const customAutocomplete = connectAutocomplete(renderAutocomplete);

  search.addWidgets([
    configure({
      filters:
        'ClassName:"URMC.ConditionPage" OR ClassName:"URMC.LocationPage" OR ClassName:"URMC.BasicContentPage" OR ClassName:"URMC.ProviderPage"',
    }),
    queryRuleCustomData({
      container: document.querySelector('[data-app-search-box]'),
      templates: {
        default: '',
      },
      transformItems(items) {
        const match = items.find((data) => Boolean(data.redirect));
        if (match && match.redirect) {
          window.location.href = match.redirect;
        }
        return [];
      },
    }),
    virtualSearchBox({}),

    customAutocomplete({
      container: document.querySelector('[data-app-search-box]'),
    }),

    hits({
      container: '[data-app-search-hits]',
      cssClasses: {
        list: 'list-none',
        item: 'shadow-none relative result-list-item',
      },
      templates: {
        item(hit, { html, components }) {
          const contentTypeLabel =
            hit['ClassName'] === 'URMC.LocationPage'
              ? html`Location <span class="mx-1">|</span> ${hit['ContentType']}`
              : hit['ClassName'] === 'URMC.BasicContentPage'
              ? 'Information'
              : hit['ClassName'] === 'URMC.ProviderPage'
              ? 'Provider'
              : hit['ContentType'];
          const referenceID = hit['objectID'];
          const profilePic =
            hit['ClassName'] === 'URMC.ProviderPage' && hit['PrimaryImage']
              ? html` <div class="w-16 mr-5">
                  <div class="rounded overflow-hidden aspect-w-5 aspect-h-7">
                    <a
                      href="${hit['Url']}"
                      id="img-${referenceID}"
                      onMouseEnter=${() => imageMouseEnter(referenceID)}
                      onMouseLeave=${() => imageMouseLeave(referenceID)}
                    >
                      <img
                        class="object-cover h-full w-full"
                        src="${hit['PrimaryImage']}"
                      />
                    </a>
                  </div>
                </div>`
              : hit['ClassName'] === 'URMC.ProviderPage' && !hit['PrimaryImage']
              ? html` <div class="w-16 mr-5">
                  <div class="rounded overflow-hidden aspect-w-5 aspect-h-7">
                    <a
                      href="${hit['Url']}"
                      id="img-${referenceID}"
                      onMouseEnter=${() => imageMouseEnter(referenceID)}
                      onMouseLeave=${() => imageMouseLeave(referenceID)}
                    >
                      <img
                        class="object-cover h-full w-full"
                        src="${placeholderImageURL}"
                      />
                    </a>
                  </div>
                </div>`
              : html``;

          const hideLine2 = hit['Address']?.Line2 ? '' : 'hidden';

          const parentHospitalDiv =
            hit['ParentHospitalTitle']?.length > 0
              ? html`
                  <div class="mt-2.5 md:mt-2.5">
                    <span class="text-base">
                      Part of ${hit['ParentHospitalTitle']}
                    </span>
                    <button
                      class="relative group cursor-pointer"
                      aria-label="What is this?"
                    >
                      <span class="ml-1 tooltip">
                        <svg
                          class="w-4 h-4 fill-current"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 512 512"
                        >
                          <path
                            d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 464c-114.7 0-208-93.31-208-208S141.3 48 256 48s208 93.31 208 208S370.7 464 256 464zM256 336c-18 0-32 14-32 32s13.1 32 32 32c17.1 0 32-14 32-32S273.1 336 256 336zM289.1 128h-51.1C199 128 168 159 168 198c0 13 11 24 24 24s24-11 24-24C216 186 225.1 176 237.1 176h51.1C301.1 176 312 186 312 198c0 8-4 14.1-11 18.1L244 251C236 256 232 264 232 272V288c0 13 11 24 24 24S280 301 280 288V286l45.1-28c21-13 34-36 34-60C360 159 329 128 289.1 128z"
                          />
                        </svg>
                      </span>
                      <span role="tooltip" class="tooltip-modal">
                        This location is an outpatient hospital department of
                        <span> ${hit['ParentHospitalTitle']}</span>. There may
                        be additional insurance or private pay charges for
                        services provided here.
                      </span>
                    </button>
                  </div>
                `
              : null;

          const resultDescription =
            hit['ClassName'] === 'URMC.ProviderPage'
              ? html`<p class="text-base mt-1 text-gray-800">
                  ${hit['RelatedDepartments'].join(', ')}
                </p>`
              : hit['ClassName'] === 'URMC.LocationPage'
              ? html` ${parentHospitalDiv}
                  <p class="text-body-regular mt-2.5">
                    <span>${hit['Address']?.Line1},</span>
                    <span class="${hideLine2}"> ${hit['Address']?.Line2},</span>
                    <span> ${hit['Address']?.City},</span>
                    <span> ${hit['Address']?.State}</span>
                    <span> ${hit['Address']?.PostalCode}</span>
                  </p>`
              : hit['ClassName'] === 'URMC.ConditionPage' ||
                hit['ClassName'] === 'URMC.BasicContentPage'
              ? html`<p class="text-body-regular mt-4">
                  ${components.Highlight({
                    attribute: 'Content',
                    hit,
                  })}
                </p>`
              : html``;

          return html`
            <span
              class="text-sm uppercase text-gray-800/80 mb-3 font-semibold block"
            >
              ${contentTypeLabel}
            </span>
            <div class="flex">
              ${profilePic}
              <div>
                <h3>
                  <a
                    class="profile-link btn-link text-navy-blue no-underline hover:underline hover:text-pacific-blue"
                    href="${hit['Url']}"
                    id="link-${hit['objectID']}"
                    onMouseEnter=${() => imageMouseEnter(referenceID)}
                    onMouseLeave=${() => imageMouseLeave(referenceID)}
                  >
                    ${components.Highlight({
                      attribute: 'Title',
                      hit,
                    })}
                  </a>
                </h3>
                ${resultDescription}
              </div>
            </div>
          `;
        },
        empty: '',
      },
    }),

    stats({
      container: '[data-app-search-stats]',
      cssClasses: {
        text: 'stats-text',
      },
      templates: {
        text(data, { html }) {
          let count = '';

          if (search.status === 'error') {
            return html`
              <div>
                <p class="text-body-regular">Search is offline</p>
                <p>
                  Sorry, it appears our search tool is currently offline or
                  being worked on. The issue should be resolved soon.
                </p>
              </div>
            `;
          } else if (data.hasManyResults) {
            count += `${data.nbHits} Results`;
          } else if (data.hasOneResult) {
            count += `1 Result`;
          } else {
            count += `Sorry, we couldn't find any results`;
            return html`
              <p class="text-body-regular">
                <strong>${count} for: "${data.query}"</strong>
              </p>
              <p class="text-body-lg mt-10">
                Try rephrasing your search, or try browsing our
                <span> </span>
                <a href="/conditions-and-treatments">condition categories</a>
                <span> </span>
                or searching our
                <span> </span>
                <a href="/people">providers</a>
                <span> </span>
                or
                <span> </span>
                <a href="/locations">locations</a>
                <span> </span>
                to find what you need.
              </p>
            `;
          }

          if (data.query) {
            return html` <p>
              <strong>${count}</strong> for
              <strong> "${data.query}"</strong>
            </p>`;
          } else {
            return html` <p>
              <strong>${count}</strong>
            </p>`;
          }
        },
      },
    }),

    stats({
      container: '[data-app-mobile-results-button]',
      templates: {
        text(data, {}) {
          let count = '';

          if (data.hasManyResults) {
            count += `View ${data.nbHits} Results`;
          } else if (data.hasOneResult) {
            count += `View 1 Result`;
          } else {
            count += `No Results Found`;
          }
          return count;
        },
      },
    }),

    stats({
      container: '[data-app-continue-searching]',
      templates: {
        text(data, { html }) {
          const clinicalTrialsUrl = `
          https://www.urmc.rochester.edu/research/health-research/clinical-trials.aspx?q=${data.query}`;

          const eventsUrl = `
          https://events.rochester.edu/search/events?search=${data.query}`;

          const educationAndResearchUrl = `
          https://www.urmc.rochester.edu/search.aspx?q=${data.query}`;

          return html`
            <p class="text-lg mb-6">
              <strong>Continue searching for "${data.query}" in:</strong>
            </p>

            <div class="space-y-6 pl-3.5 border-l-[3px] border-transparent">
              <div>
                <a href="${clinicalTrialsUrl}" class="continue-link">
                  Clinical Trials
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="inline-block w-5 h-5 fill-current ml-2 align-text-bottom"
                    viewBox="0 0 448 512"
                  >
                    <path
                      d="M280 80C266.7 80 256 69.25 256 56C256 42.75 266.7 32 280 32H424C437.3 32 448 42.75 448 56V200C448 213.3 437.3 224 424 224C410.7 224 400 213.3 400 200V113.9L200.1 312.1C191.6 322.3 176.4 322.3 167 312.1C157.7 303.6 157.7 288.4 167 279L366.1 80H280zM0 120C0 89.07 25.07 64 56 64H168C181.3 64 192 74.75 192 88C192 101.3 181.3 112 168 112H56C51.58 112 48 115.6 48 120V424C48 428.4 51.58 432 56 432H360C364.4 432 368 428.4 368 424V312C368 298.7 378.7 288 392 288C405.3 288 416 298.7 416 312V424C416 454.9 390.9 480 360 480H56C25.07 480 0 454.9 0 424V120z"
                    />
                  </svg>
                </a>
              </div>

              <div>
                <a href="${eventsUrl}" class="continue-link">
                  Events
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="inline-block w-5 h-5 fill-current ml-2 align-text-bottom"
                    viewBox="0 0 448 512"
                  >
                    <path
                      d="M280 80C266.7 80 256 69.25 256 56C256 42.75 266.7 32 280 32H424C437.3 32 448 42.75 448 56V200C448 213.3 437.3 224 424 224C410.7 224 400 213.3 400 200V113.9L200.1 312.1C191.6 322.3 176.4 322.3 167 312.1C157.7 303.6 157.7 288.4 167 279L366.1 80H280zM0 120C0 89.07 25.07 64 56 64H168C181.3 64 192 74.75 192 88C192 101.3 181.3 112 168 112H56C51.58 112 48 115.6 48 120V424C48 428.4 51.58 432 56 432H360C364.4 432 368 428.4 368 424V312C368 298.7 378.7 288 392 288C405.3 288 416 298.7 416 312V424C416 454.9 390.9 480 360 480H56C25.07 480 0 454.9 0 424V120z"
                    />
                  </svg>
                </a>
              </div>

              <div>
                <a href="${educationAndResearchUrl}" class="continue-link">
                  Education & Research
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="inline-block w-5 h-5 fill-current ml-2 align-text-bottom"
                    viewBox="0 0 448 512"
                  >
                    <path
                      d="M280 80C266.7 80 256 69.25 256 56C256 42.75 266.7 32 280 32H424C437.3 32 448 42.75 448 56V200C448 213.3 437.3 224 424 224C410.7 224 400 213.3 400 200V113.9L200.1 312.1C191.6 322.3 176.4 322.3 167 312.1C157.7 303.6 157.7 288.4 167 279L366.1 80H280zM0 120C0 89.07 25.07 64 56 64H168C181.3 64 192 74.75 192 88C192 101.3 181.3 112 168 112H56C51.58 112 48 115.6 48 120V424C48 428.4 51.58 432 56 432H360C364.4 432 368 428.4 368 424V312C368 298.7 378.7 288 392 288C405.3 288 416 298.7 416 312V424C416 454.9 390.9 480 360 480H56C25.07 480 0 454.9 0 424V120z"
                    />
                  </svg>
                </a>
              </div>
            </div>
          `;
        },
      },
    }),

    customClearFacets({
      container: document.querySelector(
        '[data-app-search-refinement="clear-facets"]'
      ),
      includedAttributes: ['ClassName'],
    }),

    menu({
      attribute: 'ClassName',
      container: '[data-app-search-refinement="page-types"]',
      sortBy: ['name:asc'],
      cssClasses: {
        list: ['pl-0 space-y-6 list-none'],
        item: ['border-l-[3px] border-transparent pl-3.5'],
      },
      templates: {
        item({ label, count }) {
          const resultType =
            label === 'URMC.LocationPage'
              ? 'Locations'
              : label === 'URMC.ConditionPage'
              ? 'Conditions & Treatments'
              : label === 'URMC.ProviderPage'
              ? 'Providers'
              : 'Information';

          return `
              <button
                class="py-0.5 text-navy-blue text-lg hover:underline focus-visible:outline-offset-4 focus-visible:outline-current"
                onload="${updateTotalHitsCount(label, count)}"
                >
                ${resultType} (${count})
              </button>
            `;
        },
      },
    }),

    pagination({
      container: '[data-app-search-pagination]',
      scrollTo: '[data-app-search-hits]',
      showFirst: true,
      showLast: true,
      padding: 2,
      cssClasses: {
        list: 'justify-center remove-space list-none custom-pagination border-t',
        item: 'w-8 h-8 flex items-center justify-center overflow-hidden',
      },
    }),
  ]);

  search.start();
}
